import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getLandingNoteLinesApi } from "api/landingNote";
import { ILandingNote, LandingNoteLine } from "interface/landingNote";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { formatWeight } from "utils/formatters"

interface LandingNotesMobileListProps {
	items: ILandingNote[];
	loading: boolean;
	onEdit: (item: ILandingNote) => void;
	onOpenPdf: (item: ILandingNote) => void;
	onToggleChecked: (item: ILandingNote) => void;
	isChecked: (id: string) => boolean;
	canCheck: (status: number) => boolean;
	onLoadMore: () => void;
	onUpload: (item: ILandingNote) => void;
}

const getStatusClassName = (status: number, signStatus?: number) => {
	if (status === 1) return "landing-note-list-card--status-red";
	if (status === 2) return "landing-note-list-card--status-yellow";
	if (status === 4 && signStatus === 0)
		return "landing-note-list-card--status-orange";
	return "landing-note-list-card--status-green";
};

const LandingNotesMobileList: FC<LandingNotesMobileListProps> = ({
	items,
	loading,
	onEdit,
	onOpenPdf,
	onToggleChecked,
	isChecked,
	canCheck,
	onLoadMore,
	onUpload,
}) => {
	const { t } = useTranslation();
	const [expandedNotes, setExpandedNotes] = useState<string[]>([]);
	const [lineMap, setLineMap] = useState<Record<string, LandingNoteLine[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<string, boolean>>(
		{},
	);

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining =
			target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	const toggleLines = (item: ILandingNote) => {
		if (expandedNotes.includes(item.noteNumber)) {
			setExpandedNotes((prev) => prev.filter((id) => id !== item.noteNumber));
			return;
		}

		setExpandedNotes((prev) => [...prev, item.noteNumber]);
		if (lineMap[item.noteNumber] || lineLoadingMap[item.noteNumber]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.noteNumber]: true }));

		getLandingNoteLinesApi({
			noteNumber: item.noteNumber as unknown as number,
			source: item.source,
		})
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.noteNumber]: (response?.data || []) as LandingNoteLine[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({ ...prev, [item.noteNumber]: false }));
			});
	};

	return (
		<div className="landing-note-list-mobile" onScroll={handleScroll}>
			{empty && (
				<div className="landing-note-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const expanded = expandedNotes.includes(item.noteNumber);
				const lines = lineMap[item.noteNumber] || [];
				const linesLoading = lineLoadingMap[item.noteNumber] === true;
				const checkAllowed = canCheck(item.noteStatusId);
				const showEdit = item.noteStatusId === 1 || item.noteStatusId === 2;
				const showUpload = item.noteStatusId === 1;
				const showSignature = item.noteStatusId === 4;
				const itemKey = `${item.noteNumber}-${item.source ?? "nosource"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`landing-note-list-card ${getStatusClassName(item.noteStatusId, item.signStatus)}`}
					>
						<div className="landing-note-list-card-header">
							<div className="landing-note-list-card-note">
								{item.noteNumber}
							</div>
							<div className="landing-note-list-card-vessel">
								{item.vesselName || "-"}
							</div>
							<div className="landing-note-list-card-date">
								<GrideDateFormatter
									date={item.landingTime}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="landing-note-list-card-body">
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{item.vesselRegistrationId || "-"}</strong>
							</div>
							<div>
								<span>{t("FishingGear")}</span>
								<strong>{item.fishingGear || "-"}</strong>
							</div>
							<div>
								<span>{t("Terminal")}</span>
								<strong>{item.receiverName || "-"}</strong>
							</div>
							<div>
								<span>{t("CatchArea")}</span>
								<strong>
									{item.catchField || "-"}
									{item.catchAreaZone ? ` / ${item.catchAreaZone}` : ""}
								</strong>
							</div>
							<div>
								<span>{t("TotalWeight")}</span>
								<strong>{formatWeight(item.netLiveWeightKg)}</strong>
							</div>
						</div>

						<div className="landing-note-list-card-actions">
							{" "}
							{showUpload && (
								<button
									type="button"
									className="landing-note-list-icon-btn"
									onClick={() => onUpload(item)}
									title={t("Upload")}
								>
									<img
										src={`/icon/${ICON_NAMES.Upload}.svg`}
										alt={t("Upload")}
									/>
								</button>
							)}{" "}
							{(showEdit || showSignature) && (
								<button
									type="button"
									className="landing-note-list-icon-btn"
									onClick={() => onEdit(item)}
									title={showSignature ? t("Signature") : t("Edit")}
								>
									<img
										src={`/icon/${showSignature ? ICON_NAMES.Signature : ICON_NAMES.Edit}.svg`}
										alt={showSignature ? t("Signature") : t("Edit")}
									/>
								</button>
							)}
							<button
								type="button"
								className="landing-note-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img
									src={`/icon/${ICON_NAMES.Download}.svg`}
									alt={t("Download")}
								/>
							</button>
							<button
								type="button"
								className="landing-note-list-icon-btn"
								onClick={() => onToggleChecked(item)}
								disabled={!checkAllowed}
								title={t("CatchCertificate")}
							>
								<img
									src={`/icon/${isChecked(item.noteNumber) ? ICON_NAMES.Checkbox : ICON_NAMES.CheckboxBlack}.svg`}
									alt={t("CatchCertificate")}
								/>
							</button>
							<button
								type="button"
								className="landing-note-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="landing-note-list-lines">
								{linesLoading && (
									<div className="landing-note-list-lines-loading">
										{t("Loading")}
									</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="landing-note-list-lines-empty">
										{t("NoRows")}
									</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.id ?? "noid"}-${line.lineNumber ?? "noline"}-${lineIndex}`}
											className="landing-note-list-line-row"
										>
											<div className="landing-note-list-line-number">
												#{line.lineNumber}
											</div>
											<div className="landing-note-list-line-product">
												{line.product || "-"}
											</div>
											<div className="landing-note-list-line-weight">
												{formatWeight(line.netLiveWeightKg)}
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && (
				<div className="landing-note-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default LandingNotesMobileList;
